/**
 * L'accesso di turno.
 *
 * Il messaggio d'errore è **uno solo** per tre cause diverse — password
 * sbagliata, utente inesistente, utente disattivato — ed è così di proposito
 * anche qui, non solo nel backend: distinguerle in schermata regalerebbe
 * l'elenco delle identità a chiunque abbia due minuti davanti a un tablet.
 */

import { useState, type FormEvent } from 'react';

import { BottoneGoogle, useGoogleClientId, useSessione } from '@errebi/sessione';

/**
 * With Google on, the Google button is the way in and the password form hides
 * behind a link: in production only the administrator has a password, as the
 * emergency door. With Google off (development, e2e) the screen is unchanged.
 */
export function Accesso() {
  const clientId = useGoogleClientId();
  const [conPassword, setConPassword] = useState(false);

  // until the API answers, draw nothing rather than a form that then vanishes
  if (clientId === undefined) return <div className="centro" />;
  if (clientId === null || conPassword) return <AccessoConPassword />;

  return <AccessoConGoogle clientId={clientId} usaPassword={() => setConPassword(true)} />;
}

function AccessoConGoogle({
  clientId,
  usaPassword,
}: {
  clientId: string;
  usaPassword: () => void;
}) {
  const { accediConGoogle } = useSessione();
  const [errore, setErrore] = useState<string | null>(null);

  async function entra(credenziale: string) {
    setErrore(null);
    try {
      await accediConGoogle(credenziale);
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'Accesso non riuscito.');
    }
  }

  return (
    <div className="centro">
      <div className="scheda">
        <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>Postazione di ricezione</h1>
        <p style={{ color: 'var(--ink2)', fontSize: 14, margin: '0 0 20px' }}>
          Entra con il tuo account Google aziendale. I gesti che scrivono il tuo nome — il fallato,
          i timer — ti chiederanno di confermare con Google.
        </p>

        <BottoneGoogle clientId={clientId} allaCredenziale={(c) => void entra(c)} />

        {errore && (
          <p className="avviso avviso--no" style={{ margin: '14px 0 0' }}>
            {errore}
          </p>
        )}

        <button
          type="button"
          className="btn"
          style={{ width: '100%', marginTop: 20 }}
          onClick={usaPassword}
        >
          Entra con utente e password
        </button>
      </div>
    </div>
  );
}

function AccessoConPassword() {
  const { accedi } = useSessione();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function invia(e: FormEvent) {
    e.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      await accedi(login, password);
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'Accesso non riuscito.');
      setPassword('');
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="centro">
      <form className="scheda" onSubmit={(e) => void invia(e)}>
        <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>Postazione di ricezione</h1>
        <p style={{ color: 'var(--ink2)', fontSize: 14, margin: '0 0 20px' }}>
          Entra a inizio turno. I gesti che scrivono il tuo nome — il fallato, i timer — ti
          chiederanno di nuovo la password.
        </p>

        <label className="campo">
          <span className="campo__k">Utente</span>
          <input
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </label>

        <label className="campo">
          <span className="campo__k">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        {errore && (
          <p className="avviso avviso--no" style={{ margin: '0 0 14px' }}>
            {errore}
          </p>
        )}

        <button className="btn btn--primario" style={{ width: '100%' }} disabled={inCorso}>
          {inCorso ? 'Un momento…' : 'Entra'}
        </button>
      </form>
    </div>
  );
}

/**
 * La richiesta di conferma: la stessa identità, per un gesto solo.
 *
 * Non è un secondo accesso e non deve sembrarlo — il messaggio dice **cosa**
 * quel gesto scrive, perché è quella l'informazione che rende la richiesta
 * ragionevole invece che molesta.
 */
export function ConfermaIdentita() {
  const { confermaInCorso, identita } = useSessione();
  const clientId = useGoogleClientId();

  if (confermaInCorso === null) return null;
  // who signed in with Google signs with Google; everyone else with the password
  if (identita?.via === 'google' && clientId) return <ConfermaConGoogle clientId={clientId} />;
  return <ConfermaConPassword />;
}

function ConfermaConGoogle({ clientId }: { clientId: string }) {
  const { confermaInCorso, rispondiAllaConfermaConGoogle, annullaConferma, identita } =
    useSessione();
  const [errore, setErrore] = useState<string | null>(null);

  async function conferma(credenziale: string) {
    setErrore(null);
    try {
      await rispondiAllaConfermaConGoogle(credenziale);
    } catch {
      setErrore(`Serve l'account Google di ${identita?.utente.login}.`);
    }
  }

  return (
    <div className="velo">
      <div className="scheda">
        <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>Conferma che sei tu</h2>
        <p style={{ color: 'var(--ink2)', fontSize: 14, margin: '0 0 18px' }}>{confermaInCorso}</p>

        <BottoneGoogle clientId={clientId} allaCredenziale={(c) => void conferma(c)} subito />

        {errore && (
          <p className="avviso avviso--no" style={{ margin: '14px 0 0' }}>
            {errore}
          </p>
        )}

        <button
          type="button"
          className="btn"
          style={{ width: '100%', marginTop: 20 }}
          onClick={annullaConferma}
        >
          Annulla
        </button>
      </div>
    </div>
  );
}

function ConfermaConPassword() {
  const { confermaInCorso, rispondiAllaConferma, annullaConferma, identita } = useSessione();
  const [password, setPassword] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function invia(e: FormEvent) {
    e.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      await rispondiAllaConferma(password);
      setPassword('');
    } catch {
      setErrore('Password non valida.');
      setPassword('');
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="velo">
      <form className="scheda" onSubmit={(e) => void invia(e)}>
        <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>Conferma che sei tu</h2>
        <p style={{ color: 'var(--ink2)', fontSize: 14, margin: '0 0 18px' }}>{confermaInCorso}</p>

        <label className="campo">
          <span className="campo__k">Password di {identita?.utente.login}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
            required
          />
        </label>

        {errore && (
          <p className="avviso avviso--no" style={{ margin: '0 0 14px' }}>
            {errore}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--primario" style={{ flex: 1 }} disabled={inCorso}>
            Conferma
          </button>
          <button type="button" className="btn" onClick={annullaConferma}>
            Annulla
          </button>
        </div>
      </form>
    </div>
  );
}
